/**
 * React auth context provider for AETHER.
 * Wraps the app and manages authentication state.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  AuthUser,
  getCurrentUser,
  exchangeCodeForTokens,
  redirectToLogin,
  logout,
  getIdToken,
  refreshTokens,
  secondsUntilExpiry,
  userHasEditAccess,
  userHasAbbreviationAdmin,
} from './authService';
import { createLogger } from '../utils/logger';

const logger = createLogger('Auth');

/** How often the background timer checks whether the token needs refreshing. */
const REFRESH_CHECK_INTERVAL_MS = 60_000;
/** Refresh proactively when fewer than this many seconds remain before expiry. */
const PROACTIVE_REFRESH_SECONDS = 600;

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  /** True when the signed-in user is in the edit-access group (e.g. aether-gamma). */
  canEdit: boolean;
  /** True when the user may manage the abbreviation list (MXO-L6 group). */
  canManageAbbreviations: boolean;
  login: () => void;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  authError: null,
  canEdit: false,
  canManageAbbreviations: false,
  login: () => {},
  logout: () => {},
  getToken: () => null,
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Guards the one-time OAuth code exchange. React 18 StrictMode invokes mount
 * effects twice in development, and OAuth authorization codes are single-use —
 * a second exchange of the same code fails with `invalid_grant`. This module
 * flag ensures the exchange runs only once per page load.
 */
let codeExchangeStarted = false;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    // Check if we're on the callback route with an auth code
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const pathname = window.location.pathname;

    if (code && (pathname === '/callback' || pathname === '/callback/')) {
      if (codeExchangeStarted) return;
      codeExchangeStarted = true;
      handleCallback(code);
    } else {
      // Check for existing session
      const existingUser = getCurrentUser();
      setUser(existingUser);
      setIsLoading(false);
    }
  }, []);

  // Proactively refresh the token in the background so a logged-in user is never
  // silently logged out mid-shift. Checks periodically and refreshes when the
  // token is close to expiry; updates user state from the refreshed token.
  useEffect(() => {
    if (!user) return;
    const checkAndRefresh = async (): Promise<void> => {
      try {
        if (secondsUntilExpiry() > PROACTIVE_REFRESH_SECONDS) return;
        const ok = await refreshTokens();
        if (ok) {
          setUser(getCurrentUser());
        } else {
          logger.warn('Background token refresh failed; session may require re-login');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        logger.error('Background token refresh error', { error: message });
      }
    };
    const intervalId = window.setInterval(() => {
      void checkAndRefresh();
    }, REFRESH_CHECK_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [user]);

  const handleCallback = async (code: string): Promise<void> => {
    try {
      setIsLoading(true);
      const authenticatedUser = await exchangeCodeForTokens(code);
      setUser(authenticatedUser);
      // Clean up the URL (remove the code parameter)
      window.history.replaceState({}, document.title, '/');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      logger.error('Auth callback failed', { error: message });
      setAuthError(message);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = useCallback((): void => {
    redirectToLogin();
  }, []);

  const handleLogout = useCallback((): void => {
    logout();
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    authError,
    canEdit: userHasEditAccess(user),
    canManageAbbreviations: userHasAbbreviationAdmin(user),
    login: handleLogin,
    logout: handleLogout,
    getToken: getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
